import React, { useMemo } from 'react';
import { toast } from 'react-toastify';

import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';

import { newCurrencyRateDrawerSections } from '@common/constants';
import { useValidateCurrencyEffectiveDate } from '@common/slices';
import { IBaseDrawerProps, INewCurrencyRateValues } from '@common/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { useSelector } from 'react-redux';
import { RootState } from '@core/redux/store';

const NewCurrencyRateDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );
  const [validateEffectiveDate] = useValidateCurrencyEffectiveDate.useLazy();

  const sections = useMemo<IDynamicFormWidgetSection[]>(() => {
    const now = new Date();
    now.setSeconds(0, 0);

    const values = { effectiveDate: now.toISOString() };
    return newCurrencyRateDrawerSections(values);
  }, []);
  const onSubmit = async (rawValues: Record<string, any>) => {
    try {
      const [data] = normalizeDynamicFormDrawerValues(sections, rawValues) as [
        INewCurrencyRateValues
      ];

      if (!data?.effectiveDate || data.effectiveDate === 'Invalid Date') {
        throw new Error('Please select a valid effective date.');
      }

      const { data: response } = await validateEffectiveDate({
        variables: {
          effectiveDate: data.effectiveDate,
          pagination: { pageNumber: 1, pageSize: 5 },
          orderBy: {
            attribute: 'baseCurrencyNavigation.code',
            descending: true
          }
        }
      });

      const items = response?.core?.ValidateCurrencyEffectiveDate?.items ?? [];

      if (items.length > 0) {
        throw new Error(
          'Currency Exchange Rate for the selected date/time already exists.'
        );
      }
      onSuccess('/configuration/currency-exchange-rate/add-ons', {
        state: { effectiveDate: rawValues.effectiveDate }
      });
    } catch (error) {
      console.error(error);
      toast.error(<ToastErrorMessage>{error.message}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormWidget
      sections={sections}
      onSubmit={onSubmit}
      submitButtonState={submitButtonState}
    />
  );
};

export { NewCurrencyRateDrawer };
